/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Modal,
  OutlinedInput,
  Typography,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import authApi from "services/authApi";
import { useAuth } from "context/AuthContext";
import { toast } from "react-toastify";
import * as Styled from "./styles";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import { useAppDispatch } from "store/hooks";

interface CreatePasswordData {
  password: string;
  passwordConfirmation: string;
}

interface CreatePasswordModalProps {
  username: string;
  password: string;
}

const CreatePasswordModal: React.FC<CreatePasswordModalProps> = ({
  username,
  password,
}) => {
  const { t } = useTranslation();
  const { needCreateNewPassword, cancelNewPasswordCreation, signIn } =
    useAuth();
  const [step, setStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CreatePasswordData>();

  const dispatch = useAppDispatch();

  // eslint-disable-next-line consistent-return
  const handleSubmitForm = async (data: CreatePasswordData) => {
    dispatch(setIsSpinnerVisible(true));
    if (step === 0) {
      try {
        await authApi.put("v1/users/password/change", {
          userName: username,
          password,
          newPassword: data.password,
          scope: "dealer",
        });

        setStep(1);
        dispatch(setIsSpinnerVisible(false));
      } catch (err: any) {
        if (err?.response?.status === 400) {
          toast("Ops, não conseguimos atualizar sua senha", {
            type: "error",
            autoClose: false,
          });
        } else {
          toast("Ops, algo deu errado. Tente novamente.", {
            type: "error",
            autoClose: false,
          });
        }
        reset();
        dispatch(setIsSpinnerVisible(false));
      }
    } else if (step === 1) {
      dispatch(setIsSpinnerVisible(true));
      await signIn({
        username,
        password: data.password,
      });
      dispatch(setIsSpinnerVisible(false));
      setIsOpen(false);
    }
  };

  useEffect(() => {
    reset();
    setIsOpen(needCreateNewPassword);
    if (!needCreateNewPassword) {
      setStep(0);
    }
  }, [needCreateNewPassword, reset]);

  const [isReset, setIsReset] = useState(false);

  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleClickShowPasswordConfirm = () =>
    setShowPasswordConfirm((show) => !show);

  const handleMouseDownPasswordConfirm = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (
      watch("password")?.length > 5 &&
      watch("password")?.length <= 20 &&
      watch("password")?.match("^(?=.*?[0-9])(?=.*?[A-Z])(?=.*?[a-z]).{1,}$")
        ?.length &&
      watch("password")?.match("^(?=.*?[#?!@$%^&*-]).{1,}$")?.length &&
      watch("password") === watch("passwordConfirmation")
    ) {
      setIsReset(true);
    } else {
      setIsReset(false);
    }
  }, [watch("password"), watch("passwordConfirmation")]);

  const modalContentObject = useMemo(() => {
    const contentObj = {
      title: "",
      subtitle: "",
      formItems: <div />,
      actionLabel: "",
    };

    if (step === 0) {
      contentObj.title = t("resetPassword");
      contentObj.subtitle = t("writeNewPassword");
      contentObj.formItems = (
        <>
          <Styled.ContainerRules>
            <FormControl
              sx={{ width: "100%", height: "40px", marginBottom: "40px" }}
              variant="outlined"
            >
              <InputLabel htmlFor="outlined-adornment-password">
                {t("passwordLabel")}
              </InputLabel>
              <OutlinedInput
                {...register("password")}
                id="outlined-adornment-password"
                type={showPassword ? "text" : "password"}
                inputProps={{
                  maxLength: 20,
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("passwordLabel")}
              />
            </FormControl>

            <Styled.ContentRules>
              <Typography variant="body1" color="text.secondary" mb={1}>
                Sua senha de acesso deve conter:
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                {watch("password")?.length > 5 &&
                watch("password")?.length <= 20 ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CancelIcon fontSize="small" />
                )}
                Entre 6 a 20 caracteres
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                {watch("password")?.match(
                  "^(?=.*?[0-9])(?=.*?[A-Z])(?=.*?[a-z]).{1,}$",
                )?.length ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CancelIcon fontSize="small" />
                )}{" "}
                Letras maiúsculas e minúsculas e números
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={1}>
                {watch("password")?.match("^(?=.*?[#?!@$%^&*-]).{1,}$")
                  ?.length ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : (
                  <CancelIcon fontSize="small" />
                )}{" "}
                Ao menos um símbolo (*@/)
              </Typography>
            </Styled.ContentRules>
          </Styled.ContainerRules>

          <FormControl
            sx={{ width: "100%", height: "40px", marginBottom: "40px" }}
            variant="outlined"
          >
            <InputLabel htmlFor="outlined-adornment-confirm-password-confirmation">
              {t("confirmPassword")}
            </InputLabel>
            <OutlinedInput
              label={t("confirmPassword")}
              {...register("passwordConfirmation")}
              id="outlined-adornment-confirm-password-confirmation"
              type={showPasswordConfirm ? "text" : "password"}
              inputProps={{
                maxLength: 20,
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPasswordConfirm}
                    onMouseDown={handleMouseDownPasswordConfirm}
                    edge="end"
                  >
                    {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>

          {watch("password") !== watch("passwordConfirmation") && (
            <Typography color="#9847f5" variant="body2" mb={1}>
              A senha não está igual
            </Typography>
          )}
        </>
      );
      contentObj.actionLabel = t("reset").toUpperCase();
    } else if (step === 1) {
      contentObj.title = t("successPasswordReset");
      contentObj.subtitle = t("canAccessAcount");
      contentObj.actionLabel = t("access");
    }

    return contentObj;
  }, [
    step,
    errors,
    watch("password"),
    watch("passwordConfirmation"),
    register,
    showPasswordConfirm,
    showPassword,
    t,
  ]);

  return (
    <Modal open={isOpen} onClose={cancelNewPasswordCreation}>
      <Styled.Content>
        <Typography variant="h6">{modalContentObject.title}</Typography>

        <Typography variant="caption" color="text.secondary" mb={2}>
          {modalContentObject.subtitle}
        </Typography>

        <Styled.Form onSubmit={handleSubmit(handleSubmitForm)}>
          {modalContentObject.formItems}
          <Button
            variant="contained"
            type="submit"
            disabled={!isReset}
            fullWidth
          >
            {modalContentObject.actionLabel}
          </Button>
        </Styled.Form>
      </Styled.Content>
    </Modal>
  );
};

export default CreatePasswordModal;
